import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { rutaFicha } from '#components/mobile/budget/useFichaCategoria';
import { usePagosFijos } from '#components/mobile/budget/usePagosFijos';
import {
  AccionTexto,
  Tarjeta,
  VacioCompacto,
} from '#components/mobile/inicio/comunes';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import { useFormatosGavi } from '#components/mobile/pagosGavi/formatos';
import { textoConsejo } from '#components/mobile/pagosGavi/textosConsejo';
import { usePagosGavi } from '#components/mobile/pagosGavi/usePagosGavi';
import { Gavi } from '#components/mobile/travesia/Gavi';
import { BarraProgreso } from '#components/mobile/ui/BarraProgreso';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { Importe } from '#components/mobile/ui/Importe';
import { color, num, texto } from '#components/mobile/ui/tokens';
import { useNavigate } from '#hooks/useNavigate';

/**
 * Pagos fijos en Inicio (opcional, desde Personalizar Inicio): lo que queda
 * por pagar este mes y, según el tamaño, el próximo cobro y lo que dice
 * Gavi (Altamar).
 *
 * Tamaños: compacto = lo que queda y la barra; normal = + el próximo cobro;
 * grande = + la frase de Gavi del aviso más urgente.
 */
export function PagosFijos({ tamano, month }: PropsWidget) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatos = useFormatosGavi();
  const pagos = usePagosFijos(month);
  const gavi = usePagosGavi(pagos.fijos);

  const titulo = t('Fixed payments');
  const ver = (
    <AccionTexto onPress={() => void navigate('/anual')}>
      <Trans>See</Trans>
    </AccionTexto>
  );

  if (pagos.cargando) {
    return null;
  }
  if (pagos.mensuales.length === 0) {
    return (
      <Tarjeta titulo={titulo} sinTarjeta data-testid="inicio-pagos-fijos">
        <VacioCompacto
          texto={t('Mark your bills as fixed payments to see them here.')}
          accion={t('See')}
          onAccion={() => void navigate('/anual')}
        />
      </Tarjeta>
    );
  }

  const { pagado, total } = pagos.resumen;
  const queda = Math.max(0, total - pagado);
  const proximo = pagos.mensuales.find(
    p => p.estado !== 'pagado' && p.tocaEsteMes !== false,
  );
  const consejo =
    tamano === 'grande'
      ? [...gavi.consejos.values()]
          .flat()
          .sort((a, b) => a.prioridad - b.prioridad)[0]
      : undefined;

  return (
    <Tarjeta titulo={titulo} accion={ver} data-testid="inicio-pagos-fijos">
      <View style={{ gap: 10 }}>
        <View style={{ gap: 2 }}>
          <Text style={{ ...texto.etiqueta, color: color.fg3 }}>
            {queda > 0 ? (
              <Trans>Left to pay this month</Trans>
            ) : (
              <Trans>All paid this month</Trans>
            )}
          </Text>
          <Text
            data-testid="inicio-pagos-fijos-queda"
            style={{ ...texto.heroe, ...num, color: color.fg }}
          >
            <Importe valor={queda > 0 ? queda : total} conEuro />
          </Text>
        </View>
        <BarraProgreso
          valor={total > 0 ? pagado / total : 1}
          color={color.ok}
          aria-label={t('Paid of the fixed payments this month')}
        />
        {tamano !== 'compacto' && proximo && (
          <View
            data-testid="inicio-pagos-fijos-proximo"
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              borderTop: `1px solid ${color.line}`,
              paddingTop: 10,
            }}
          >
            <Text
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: color.fg3,
                ...num,
              }}
            >
              {proximo.dia != null
                ? t('Day {{day}}', { day: proximo.dia })
                : t('Next')}
            </Text>
            <Text
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 15,
                fontWeight: 700,
                color: color.fg,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {(() => {
                const { emoji, resto } = separarEmoji(proximo.nombre);
                return emoji ? `${emoji} ${resto}` : resto;
              })()}
            </Text>
            {(proximo.importeCargo ?? proximo.importe) != null && (
              <Importe
                valor={proximo.importeCargo ?? proximo.importe ?? 0}
                conEuro
              />
            )}
          </View>
        )}
        {consejo && (
          <Button
            variant="bare"
            data-testid="inicio-pagos-fijos-gavi"
            onPress={() => void navigate(rutaFicha(consejo.categoria, month))}
            style={{
              flexDirection: 'row',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: 0,
              backgroundColor: 'transparent',
              textAlign: 'left',
            }}
          >
            <Gavi ancho={40} style={{ flexShrink: 0 }} />
            <Text style={{ ...texto.cuerpo, flex: 1, color: color.fg2 }}>
              {textoConsejo(t, formatos, consejo).titulo}
            </Text>
          </Button>
        )}
        {tamano === 'grande' && !consejo && gavi.activo && (
          <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
            <Gavi ancho={40} style={{ flexShrink: 0 }} />
            <Text style={{ ...texto.cuerpo, flex: 1, color: color.fg3 }}>
              <Trans>
                Gavi is watching your bills and will tell you if one goes up.
              </Trans>
            </Text>
          </View>
        )}
      </View>
    </Tarjeta>
  );
}
